import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { organizationRepository } from 'api/organizationRepository';
import InputField from 'components/fields/InputField';
import React, { useState } from 'react';
import { plantSchema } from 'validations/Validations';
import { z } from 'zod';

type AddPlantModalProps = {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  onPlantAdded: (updatedBranches: any[]) => void;
  showCenteredAlert: (
    msg: string,
    status?: 'success' | 'info' | 'warning' | 'error'
  ) => void;
};

const AddPlantModal: React.FC<AddPlantModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  onPlantAdded,
  showCenteredAlert,
}) => {
  const [newPlant, setNewPlant] = useState({
    name: '',
    isActive: true as boolean | undefined,
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
    },
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isAdding, setIsAdding] = useState(false);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const inputBg = useColorModeValue('white', 'navy.700');
  const menuListBg = useColorModeValue('white', 'navy.800');
  const placeholderColor = useColorModeValue('gray.500', 'gray.400');

  // Live per-field validation
  const validateField = (field: string, value: any, parent?: string) => {
    try {
      if (parent) {
        const parentShape = (plantSchema.shape as any)[parent] as
          | z.ZodObject<any>
          | undefined;
        const schemaField = parentShape?.shape?.[field];
        if (!schemaField) return;

        schemaField.parse(value);
        setErrors((prev) => ({ ...prev, [field]: '' }));
      } else {
        const schemaField = (plantSchema.shape as Record<string, z.ZodTypeAny>)[field];
        if (!schemaField) return;

        schemaField.parse(value);
        setErrors((prev) => ({ ...prev, [field]: '' }));
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        const zodErr = err as z.ZodError;
        setErrors((prev) => ({
          ...prev,
          [field]: zodErr.issues[0]?.message || 'Invalid value',
        }));
      }
    }
  };

  const handleChange = (field: string, value: string, parent?: string) => {
    if (field === 'isActive' && !parent) {
      const boolValue = value === 'true';
      setNewPlant((prev) => ({ ...prev, isActive: boolValue }));
      validateField(field, boolValue);
      return;
    }

    if (parent) {
      setNewPlant((prev) => ({
        ...prev,
        [parent]: { ...(prev as any)[parent], [field]: value },
      }));
      validateField(field, value, parent);
    } else {
      setNewPlant((prev) => ({ ...prev, [field]: value }));
      validateField(field, value);
    }
  };

  const handleAddPlant = async () => {
    const payloadForValidation = {
      name: newPlant.name,
      isActive: newPlant.isActive,
      address: {
        street: newPlant.address.street,
        city: newPlant.address.city,
        state: newPlant.address.state,
        zipCode: newPlant.address.zipCode,
        country: newPlant.address.country,
      },
    };

    const result = plantSchema.safeParse(payloadForValidation);

    if (!result.success) {
      const mappedErrors: Record<string, string> = {};
      result.error.issues.forEach((issue) => {
        const path = issue.path;
        const key = path.length ? String(path[path.length - 1]) : 'form';
        if (!mappedErrors[key]) mappedErrors[key] = issue.message;
      });

      setErrors((prev) => ({ ...prev, ...mappedErrors }));
      showCenteredAlert('Please fix the validation errors.', 'warning');
      return;
    }

    setErrors({});

    const payloadForAPI = {
      name: newPlant.name,
      isActive: newPlant.isActive,
      address: {
        ...newPlant.address,
        name: newPlant.name,
      },
    };

    try {
      setIsAdding(true);
      await organizationRepository.addBranch(organizationId, payloadForAPI);

      const updatedOrg = await organizationRepository.getOrganizationById(
        organizationId
      );
      onPlantAdded(updatedOrg?.locations || []);

      showCenteredAlert('Plant added successfully', 'success');

      // Reset form
      setNewPlant({
        name: '',
        isActive: true,
        address: { street: '', city: '', state: '', zipCode: '', country: '' },
      });

      onClose();
    } catch (err) {
      showCenteredAlert('Failed to add plant', 'error');
    } finally {
      setIsAdding(false);
    }
  };

  const cardBg = useColorModeValue('white', 'navy.800');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent bg={cardBg}>
        <ModalHeader>Add New Plant</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <VStack spacing={6} align="stretch">
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb="8px">
                  Plant Name
                </Text>
                <InputField
                  placeholder="Plant Name"
                  value={newPlant.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('name', e.target.value)}
                  mb={1}
                  fontSize="sm"
                />
                {errors.name && <Text fontSize="xs" color="red.500">{errors.name}</Text>}
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="bold" mb="8px">
                  Street
                </Text>
                <InputField
                  placeholder="Street"
                  value={newPlant.address.street}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('street', e.target.value, 'address')}
                  mb={1}
                  fontSize="sm"
                />
                {errors.street && <Text fontSize="xs" color="red.500">{errors.street}</Text>}
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="bold" mb="8px">
                  City
                </Text>
                <InputField
                  placeholder="City"
                  value={newPlant.address.city}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('city', e.target.value, 'address')}
                  mb={1}
                  fontSize="sm"
                />
                {errors.city && <Text fontSize="xs" color="red.500">{errors.city}</Text>}
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="bold" mb="8px">
                  Status
                </Text>
                <Select
                  placeholder="Select Status"
                  value={newPlant.isActive === true ? 'true' : 'false'}
                  onChange={(e) => handleChange('isActive', e.target.value)}
                  mb={1}
                  bg={inputBg}
                  color={textColor}
                >
                  <option
                    value="true"
                    style={{ backgroundColor: menuListBg, color: textColor }}
                  >
                    Active
                  </option>
                  <option
                    value="false"
                    style={{ backgroundColor: menuListBg, color: textColor }}
                  >
                    Inactive
                  </option>
                </Select>
                {errors.isActive && <Text fontSize="xs" color="red.500">{errors.isActive}</Text>}
              </Box>
            </VStack>

            <VStack spacing={6} align="stretch">
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb="8px">
                  State
                </Text>
                <InputField
                  placeholder="State"
                  value={newPlant.address.state}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('state', e.target.value, 'address')}
                  mb={1}
                  fontSize="sm"
                />
                {errors.state && <Text fontSize="xs" color="red.500">{errors.state}</Text>}
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="bold" mb="8px">
                  ZIP Code
                </Text>
                <InputField
                  placeholder="ZIP Code"
                  value={newPlant.address.zipCode}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('zipCode', e.target.value, 'address')}
                  mb={1}
                  fontSize="sm"
                />
                {errors.zipCode && <Text fontSize="xs" color="red.500">{errors.zipCode}</Text>}
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="bold" mb="8px">
                  Country
                </Text>
                <InputField
                  placeholder="Country"
                  value={newPlant.address.country}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('country', e.target.value, 'address')}
                  mb={1}
                  fontSize="sm"
                />
                {errors.country && <Text fontSize="xs" color="red.500">{errors.country}</Text>}
              </Box>
            </VStack>
          </SimpleGrid>
        </ModalBody>
        <ModalFooter justifyContent="center">
          <Button onClick={handleAddPlant} colorScheme="blue" mr={3} isLoading={isAdding}>
            Add
          </Button>
          <Button variant="outline" borderColor="gray.300" onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddPlantModal;
